import { cn } from '@/lib/utils';

export interface SparklineProps {
  /** Oldest first. Fewer than two and there is no trend to draw. */
  points: readonly number[];
  /** The scale the line is drawn against. A percentage series is always 0–100. */
  min?: number;
  max?: number;
  /**
   * What the line says, read out in place of the picture — "Attendance trend
   * for Aarav Mehta, rising". The shape itself is `aria-hidden`: a screen
   * reader gets the sentence, not a description of an SVG.
   */
  label: string;
  /** A `stroke-*` utility. Literal, because a trend is a series colour, not a token. */
  stroke?: string;
  className?: string;
}

/** The box the line is drawn in. One place, so the maths below cannot drift. */
const WIDTH = 88;
const HEIGHT = 26;
const PADDING = 3;

/**
 * A wordless line showing which way a series has gone.
 *
 * Sits in a table cell beside the number it belongs to — the figure says how
 * much, the line says whether it is getting better or worse, which a single
 * percentage cannot. Deliberately unlabelled and unaxised: at this size a tick
 * or a legend is noise, and the cell next to it already carries the value.
 *
 * Drawn as a smooth curve rather than straight segments so a fortnight of daily
 * points does not read as a saw blade.
 */
export function Sparkline({
  points,
  min = 0,
  max = 100,
  label,
  stroke = 'stroke-emerald-500',
  className,
}: SparklineProps) {
  // One point is a value, not a trend — saying so beats drawing a dot and
  // letting it read as a flat run.
  if (points.length < 2) {
    return (
      <span className={cn('block text-xs font-semibold text-ink-3', className)} title={label}>
        —
      </span>
    );
  }

  const span = max - min || 1;
  const plotted = points.map((value, index) => ({
    x: PADDING + (index / (points.length - 1)) * (WIDTH - PADDING * 2),
    // Clamped, so a value outside the declared scale bends the line rather than
    // escaping the box and overdrawing the neighbouring cell.
    y:
      HEIGHT -
      PADDING -
      (Math.min(Math.max(value, min), max) - min) / span * (HEIGHT - PADDING * 2),
  }));

  const path = plotted.reduce((drawn, point, index) => {
    if (index === 0) return `M ${point.x.toFixed(2)},${point.y.toFixed(2)}`;

    const previous = plotted[index - 1];
    const midX = (previous.x + point.x) / 2;

    return `${drawn} C ${midX.toFixed(2)},${previous.y.toFixed(2)} ${midX.toFixed(2)},${point.y.toFixed(2)} ${point.x.toFixed(2)},${point.y.toFixed(2)}`;
  }, '');

  return (
    <span className={cn('inline-block align-middle', className)} role="img" aria-label={label}>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        width={WIDTH}
        height={HEIGHT}
        className="overflow-visible"
        aria-hidden
      >
        <path
          d={path}
          fill="none"
          strokeWidth="1.75"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={stroke}
        />
      </svg>
    </span>
  );
}
